import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// just for archive purposes
// const fpv_colors = {
//   aqua: '#3494BA',
//   aqua2: '#58B6C0',
//   teal: '#75BDA7',
//   teal2: '#7A8C8E',
// };

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F8F9FA',
  200: '#E9ECEF',
  300: '#DEE2E6',
  400: '#CED4DA',
  500: '#ADB5BD',
  600: '#6C757D',
  700: '#495057',
  800: '#343A40',
  900: '#212529',
};

const PRIMARY = {
  lighter: '#CFE2FF',
  light: '#9EC5FE',
  main: '#3494BA', // #3494BA
  dark: '#0464FF', // #0A58CA
  darker: '#052C65',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#E2E3E5',
  light: '#C4C8CB',
  main: '#6C757D',
  dark: '#495057',
  darker: '#2B2F32',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#CFF4FC',
  light: '#9EEAF9',
  main: '#0DCAF0',
  dark: '#055160',
  darker: '#003768',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#D1E7DD',
  light: '#A3CFBB',
  main: '#198754',
  dark: '#0A3622',
  darker: '#005249',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF3CD',
  light: '#FFE69C',
  main: '#FFC107',
  dark: '#664D03',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#F8D7DA',
  light: '#F1AEB5',
  main: '#DC3545',
  dark: '#58151C',
  darker: '#7A0916',
  contrastText: '#fff',
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: '#1B1E22',
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[400],
    },
  } as const;

  return themeMode === 'dark' ? light : dark;
}
